import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminGet, adminPost, adminPatch, adminDelete } from "../../lib/adminApi";
import { ErrorBanner, OvergeslagenBanner, formatDate } from "./AdminUI";

function getSpeechRecognition() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function NotitieVeld({ entiteitType, entiteitId }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [invoerMethode, setInvoerMethode] = useState("typen");
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);

  const q = useQuery({
    queryKey: ["notities", entiteitType, entiteitId],
    queryFn: () => adminGet("/api/admin/notities", { entiteit_type: entiteitType, entiteit_id: entiteitId, limit: 50 }),
    enabled: Boolean(entiteitId),
  });

  const createMut = useMutation({
    mutationFn: (body) => adminPost("/api/admin/notities", body),
    onSuccess: () => {
      setDraft("");
      setInvoerMethode("typen");
      qc.invalidateQueries({ queryKey: ["notities", entiteitType, entiteitId] });
      toast.success("Notitie opgeslagen");
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Opslaan mislukt"),
  });

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
        recognitionRef.current = null;
      }
    };
  }, []);

  function startVoice() {
    const SR = getSpeechRecognition();
    if (!SR) {
      toast.error("Spraakherkenning niet beschikbaar in deze browser. Gebruik Chrome of Edge.");
      return;
    }
    const recognition = new SR();
    recognition.lang = "nl-NL";
    recognition.continuous = true;
    recognition.interimResults = true;
    let finalTranscript = "";
    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + " ";
        } else {
          interim += transcript;
        }
      }
      setDraft((prev) => {
        const base = prev.endsWith(finalTranscript) ? prev : prev + finalTranscript;
        return base + (interim ? " " + interim : "");
      });
    };
    recognition.onerror = (event) => {
      toast.error(`Spraak-fout: ${event.error}`);
      setIsRecording(false);
    };
    recognition.onend = () => {
      setIsRecording(false);
    };
    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
    setInvoerMethode("voice");
  }

  function stopVoice() {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
    }
    setIsRecording(false);
  }

  function submit() {
    if (!draft.trim()) return;
    createMut.mutate({
      entiteit_type: entiteitType,
      entiteit_id: entiteitId,
      inhoud: draft.trim(),
      invoer_methode: invoerMethode,
    });
  }

  const items = q.data?.items || [];
  const voiceSupported = Boolean(getSpeechRecognition());

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm" data-testid="notitie-veld">
      <h2 className="mb-3 text-sm font-semibold text-slate-700">Notities</h2>

      <div className="space-y-2">
        <textarea
          value={draft}
          onChange={(e) => { setDraft(e.target.value); if (!isRecording) setInvoerMethode("typen"); }}
          placeholder="Typ een notitie of klik op de microfoon om in te spreken…"
          rows={4}
          data-testid="notitie-draft"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm placeholder-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {voiceSupported ? (
              isRecording ? (
                <button
                  type="button"
                  onClick={stopVoice}
                  data-testid="btn-voice-stop"
                  className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                >
                  <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
                  Stop opname
                </button>
              ) : (
                <button
                  type="button"
                  onClick={startVoice}
                  data-testid="btn-voice-start"
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  title="Inspreken (Chrome/Edge)"
                >
                  Microfoon
                </button>
              )
            ) : (
              <span className="text-xs text-slate-400" title="Open in Chrome of Edge voor spraakherkenning">Microfoon niet beschikbaar</span>
            )}
            {invoerMethode === "voice" && draft ? (
              <span className="text-xs text-slate-500" data-testid="invoer-methode-indicator">Ingesproken</span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || createMut.isPending}
            data-testid="btn-notitie-opslaan"
            className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {createMut.isPending ? "Opslaan…" : "Notitie opslaan"}
          </button>
        </div>
      </div>

      <ErrorBanner error={q.error} />
      <OvergeslagenBanner
        aantal={q.data?.overgeslagen} enkelvoud="notitie" meervoud="notities"
        testid="notities-overgeslagen"
      />

      <div className="mt-6 space-y-3" data-testid="notitie-lijst">
        {q.isLoading ? (
          <div className="text-sm text-slate-400">Notities laden…</div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400">
            Nog geen notities.
          </div>
        ) : (
          items.map((n) => <NotitieRegel key={n.notitie_id} n={n} entiteitType={entiteitType} entiteitId={entiteitId} />)
        )}
      </div>
    </div>
  );
}

function NotitieRegel({ n, entiteitType, entiteitId }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(n.inhoud);

  const updateMut = useMutation({
    mutationFn: (body) => adminPatch(`/api/admin/notities/${n.notitie_id}`, body),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["notities", entiteitType, entiteitId] });
      toast.success("Notitie bijgewerkt");
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Bijwerken mislukt"),
  });

  const deleteMut = useMutation({
    mutationFn: () => adminDelete(`/api/admin/notities/${n.notitie_id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notities", entiteitType, entiteitId] });
      toast.success("Notitie verwijderd");
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Verwijderen mislukt"),
  });

  return (
    <article className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`notitie-${n.notitie_id}`}>
      <header className="mb-2 flex items-center justify-between text-xs text-slate-500">
        <span>
          <span className="font-medium text-slate-700">{n.aangemaakt_door_naam || "Onbekend"}</span>
          {" · "}{formatDate(n.created_at)}
          {n.invoer_methode === "voice" ? <span className="ml-1.5 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-600">Ingesproken</span> : null}
          {n.updated_at && n.updated_at !== n.created_at ? <span className="ml-1.5 italic">bewerkt</span> : null}
        </span>
        {!editing ? (
          <span className="flex gap-2">
            <button type="button" onClick={() => setEditing(true)} className="text-blue-600 hover:underline" data-testid={`btn-edit-${n.notitie_id}`}>Bewerken</button>
            <button type="button" onClick={() => { if (confirm("Notitie verwijderen?")) deleteMut.mutate(); }} className="text-rose-600 hover:underline" data-testid={`btn-delete-${n.notitie_id}`}>Verwijderen</button>
          </span>
        ) : null}
      </header>
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
            data-testid={`notitie-edit-${n.notitie_id}`}
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setEditing(false); setDraft(n.inhoud); }} className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700">Annuleren</button>
            <button type="button" disabled={updateMut.isPending || !draft.trim()} onClick={() => updateMut.mutate({ inhoud: draft.trim() })} className="rounded-md bg-blue-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">{updateMut.isPending ? "Opslaan…" : "Opslaan"}</button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm text-slate-700">{n.inhoud}</p>
      )}
    </article>
  );
}
